import type { Object3D, Scene } from "three";

/** O pedaço do `renderer.info` que a contagem lê (o three r186 só zera com `autoReset`). */
export interface RenderInfo {
  render: { calls: number; triangles: number };
  reset(): void;
}

/**
 * Draw calls e triângulos de um quadro (F52). `drawCalls` e `triangles` são só os da cena principal
 * (o sentido do doc 05); `postCalls`, os do pós-processamento; `frameCalls`, tudo o que o quadro
 * desenhou (ícones dos itens, a cena e o pós).
 */
export interface FrameSnapshot {
  drawCalls: number;
  triangles: number;
  postCalls: number;
  frameCalls: number;
}

const EMPTY: FrameSnapshot = { drawCalls: 0, triangles: 0, postCalls: 0, frameCalls: 0 };

/**
 * Contagem do quadro (F52). O three zera o `info` a cada `render()`; com o compositor, o último
 * render do quadro seria o triângulo do pós, e os ícones, as sondas e a cena se apagariam uns aos
 * outros. Com `info.autoReset = false` (`createRenderer`), `begin` zera no começo de cada quadro do
 * laço, a cena principal se mede pelos próprios `onBeforeRender`/`onAfterRender` (`watch`), o
 * compositor por `measurePost` e `end` guarda a foto que `stats()` e os HUDs leem. Uma sonda
 * desenhada fora do laço não mexe na foto.
 */
export class FrameStats {
  private readonly info: RenderInfo;
  private sceneCalls = 0;
  private sceneTriangles = 0;
  private postCalls = 0;
  private startCalls = 0;
  private startTriangles = 0;
  private snapshot: FrameSnapshot = EMPTY;

  constructor(info: RenderInfo) {
    this.info = info;
  }

  /** A foto do último quadro completo. */
  get last(): FrameSnapshot {
    return this.snapshot;
  }

  /** Começo do quadro do laço: zera o `info` e os acumulados. */
  begin(): void {
    this.info.reset();
    this.sceneCalls = 0;
    this.sceneTriangles = 0;
    this.postCalls = 0;
  }

  /** A cena principal começou a ser desenhada (no alvo que for: canvas, compositor ou sonda). */
  sceneStart(): void {
    this.startCalls = this.info.render.calls;
    this.startTriangles = this.info.render.triangles;
  }

  /** A cena principal acabou de ser desenhada. */
  sceneEnd(): void {
    this.sceneCalls += this.info.render.calls - this.startCalls;
    this.sceneTriangles += this.info.render.triangles - this.startTriangles;
  }

  /** Desenha com o compositor: o que ele desenhar além da cena principal conta como pós. */
  measurePost(draw: () => void): void {
    const calls = this.info.render.calls;
    const scene = this.sceneCalls;
    try {
      draw();
    } finally {
      this.postCalls += this.info.render.calls - calls - (this.sceneCalls - scene);
    }
  }

  /** Fim do quadro do laço: guarda a foto. */
  end(): FrameSnapshot {
    this.snapshot = { drawCalls: this.sceneCalls, triangles: this.sceneTriangles, postCalls: this.postCalls, frameCalls: this.info.render.calls };
    return this.snapshot;
  }

  /**
   * Mede a cena pelos ganchos dela (o three chama os da `Scene` no começo e no fim de cada
   * `render`); devolve a função que tira os ganchos (e devolve os de antes).
   */
  watch(scene: Scene): () => void {
    const before = scene.onBeforeRender;
    const after = scene.onAfterRender;
    const start = () => this.sceneStart();
    const end = () => this.sceneEnd();
    scene.onBeforeRender = function (this: Object3D, ...args: Parameters<Scene["onBeforeRender"]>) {
      start();
      before.apply(this, args);
    };
    scene.onAfterRender = function (this: Object3D, ...args: Parameters<Scene["onAfterRender"]>) {
      after.apply(this, args);
      end();
    };
    return () => {
      scene.onBeforeRender = before;
      scene.onAfterRender = after;
    };
  }
}
